// ✅ Trekkin.app — parseGPX.js v4.7.0 (Universal Elevation SAFE)
// --------------------------------------------------------------
// 🔹 Parsing turbo com chunks (800 pts/loop) e callback incremental (onChunk)
// 🔹 Compatível com Garmin, Wikiloc, Strava, Komoot, etc.
// 🔹 Reduz automaticamente trilhos >6000 pontos
// 🔹 Corrige bugs antigos:
//    • NUNCA mais mexe na string XML (sem regex assassino de namespaces)
//    • Coordenadas 0.0 já não são descartadas à toa
// 🔹 Elevação:
//    • Usa SEMPRE o <ele> original do GPX (opção A, como combinámos)
//    • Se o ficheiro não tiver <ele> NENHUM → elevStats = null (sem “ganho 0” falso)
// 🔹 Estatísticas de elevação:
//    • elevStats.totalGain  → GANHO POSITIVO acumulado
//    • elevStats.gain       → alias de totalGain
//    • elevStats.totalLoss  → perda total (descidas)
//    • elevStats.range      → max - min
//    • Todos em metros inteiros

function isParseGpxDebugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.__TREKKIN_DEBUG_PARSE_GPX__ === true ||
      window.localStorage.getItem("trekkin.debug.parsegpx") === "1"
    );
  } catch {
    return false;
  }
}

function parseGpxDebugLog(...args) {
  if (isParseGpxDebugEnabled()) console.log(...args);
}

function parseGpxDebugInfo(...args) {
  if (isParseGpxDebugEnabled()) console.info(...args);
}

function parseGpxDebugTime(label) {
  if (isParseGpxDebugEnabled()) console.time(label);
}

function parseGpxDebugTimeEnd(label) {
  if (isParseGpxDebugEnabled()) console.timeEnd(label);
}

export async function parseGPX(xmlString, onChunk, options = {}) {
  try {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlString, "application/xml");

    // Nome e descrição (fallbacks simpáticos)
    const name =
      xml.querySelector("metadata > name")?.textContent?.trim() ||
      xml.querySelector("trk > name")?.textContent?.trim() ||
      xml.querySelector("name")?.textContent?.trim() ||
      "Trilho sem nome";

    const desc =
      xml.querySelector("metadata > desc")?.textContent?.trim() ||
      xml.querySelector("trk > desc")?.textContent?.trim() ||
      xml.querySelector("desc")?.textContent?.trim() ||
      "";

    // Pontos de track/route
    const pts = Array.from(xml.querySelectorAll("trkpt, rtept"));
    if (!pts.length) {
      console.warn("⚠️ GPX vazio ou formato desconhecido");
      return { points: [], meta: { name, desc }, elevStats: null };
    }

    const chunkSize = 800;
    const parsed = [];
    const heartRateSamples = [];
    const dedicatedHeartRateSamples = Array.from(
      xml.getElementsByTagNameNS("https://trekkin.pt/gpx/1", "hrSample")
    ).map((node) => ({
      bpm: Math.round(Number(node.textContent)),
      ts: Date.parse(node.getAttribute("ts") || ""),
      source: "gpx",
    })).filter((sample) =>
      Number.isFinite(sample.ts) &&
      Number.isFinite(sample.bpm) &&
      sample.bpm >= 25 && sample.bpm <= 260
    );
    let hasAnyEle = false;

    parseGpxDebugTime(`⚡ GPX Turbo Parse (${name})`);
    for (let i = 0; i < pts.length; i += 1) {
      const p = pts[i];

      const lat = parseFloat(p.getAttribute("lat") || "");
      const lon = parseFloat(p.getAttribute("lon") || "");

      // Coordenadas inválidas → ignora
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

      // Elevação: usa SEMPRE o <ele> “normal”
      let ele = 0;
      const eleNode = p.querySelector("ele");
      if (eleNode && eleNode.textContent != null) {
        const parsedEle = parseFloat(eleNode.textContent.trim());
        if (Number.isFinite(parsedEle)) {
          ele = parsedEle;
          hasAnyEle = true;
        }
      }

      // Tempo
      const timeText = p.querySelector("time")?.textContent;
      let time = Date.now();
      if (timeText) {
        const t = Date.parse(timeText);
        if (!Number.isNaN(t)) {
          time = t;
        }
      }

      const heartRateText =
        p.getElementsByTagNameNS(
          "http://www.garmin.com/xmlschemas/TrackPointExtension/v1",
          "hr"
        )?.[0]?.textContent || p.querySelector("hr")?.textContent;
      const heartRateBpm = Number(heartRateText);
      if (
        !dedicatedHeartRateSamples.length &&
        Number.isFinite(heartRateBpm) &&
        heartRateBpm >= 25 &&
        heartRateBpm <= 260
      ) {
        heartRateSamples.push({
          bpm: Math.round(heartRateBpm),
          ts: time,
          source: "gpx",
        });
      }

      parsed.push({ lat, lon, ele, time });

      // Callback incremental (se fornecido)
      if (onChunk && i % chunkSize === 0) {
        try {
          onChunk([...parsed]);
        } catch (e) {
          console.warn("⚠️ Erro no onChunk do parseGPX:", e);
        }
        // ceder 1 frame ao main thread
        // (no browser, isto evita UI a engasgar com GPX gigantes)
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 3));
      }
    }
    if (dedicatedHeartRateSamples.length) {
      heartRateSamples.push(...dedicatedHeartRateSamples);
      heartRateSamples.sort((a, b) => a.ts - b.ts);
    }
    parseGpxDebugTimeEnd(`⚡ GPX Turbo Parse (${name})`);

    // 🔽 Redução opcional de densidade (desativada por defeito para máxima fidelidade)
    let points = parsed;
    const maxPoints = Number(options?.maxPoints);
    if (Number.isFinite(maxPoints) && maxPoints > 0 && parsed.length > maxPoints) {
      const step = Math.ceil(parsed.length / maxPoints);
      points = parsed.filter((_, i) => i % step === 0);
      parseGpxDebugInfo(`🧩 GPX reduzido ${parsed.length} → ${points.length} pontos`);
    }

    // 📊 Estatísticas de elevação — só se houver <ele> REAL no ficheiro
    let elevStats = null;

    if (hasAnyEle && points.length > 1) {
      let minEle = Infinity;
      let maxEle = -Infinity;
      let totalGain = 0;
      let totalLoss = 0;
      let lastEle = null;

      const threshold = 1.5; // ignora ruído < 1.5 m

      for (const pt of points) {
        const ele = Number.isFinite(pt.ele) ? pt.ele : 0;

        if (ele < minEle) minEle = ele;
        if (ele > maxEle) maxEle = ele;

        if (lastEle != null) {
          let diff = ele - lastEle;

          // Suavização: ignora micro-oscilações
          if (Math.abs(diff) < threshold) {
            diff = 0;
          }

          if (diff > 0) totalGain += diff;
          else if (diff < 0) totalLoss += -diff;
        }

        lastEle = ele;
      }

      if (!Number.isFinite(minEle)) {
        minEle = 0;
        maxEle = 0;
      }

      elevStats = {
        min: Math.round(minEle),
        max: Math.round(maxEle),
        gain: Math.round(totalGain),
        totalGain: Math.round(totalGain),
        totalLoss: Math.round(totalLoss),
        range: Math.round(maxEle - minEle),
      };
    }

    const statsMsg =
      elevStats && Number.isFinite(elevStats.totalGain)
        ? `, ganho acumulado ${elevStats.totalGain} m`
        : ", sem dados de elevação no ficheiro";

    parseGpxDebugLog(
      `📘 GPX processado: ${name} (${points.length} pts${statsMsg})`
    );

    const heartRateValues = heartRateSamples.map((sample) => sample.bpm);
    const heartRate = heartRateValues.length
      ? {
          sampleCount: heartRateValues.length,
          averageBpm: Math.round(
            heartRateValues.reduce((sum, value) => sum + value, 0) /
              heartRateValues.length
          ),
          minBpm: Math.min(...heartRateValues),
          maxBpm: Math.max(...heartRateValues),
          source: "gpx",
          samples: heartRateSamples,
        }
      : null;

    return {
      points,
      meta: { name, desc },
      elevStats,
      heartRate,
    };
  } catch (err) {
    console.error("❌ Erro no parseGPX:", err);
    return { points: [], meta: {}, elevStats: null };
  }
}
